import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'

const UpdateRegistration = ({
  initialData = null,
  onSubmit = () => {},
  onCancel = () => {},
  loading = false,
}) => {
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState({
    enrollment_number: '',
    registration_date: '',
    session: '',
    name: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    father_occupation: '',
    mother_name: '',
    phone_number: '',
    parent_phone_number: '',
    email: '',
    class: '',
    course: '',
    course_duration: '',
    school_or_college_name: '',
    subject_or_stream: '',
    address: '',
    city: '',
    pin_code: '',
    fee: '',
    due_fee: '',
    discount: '',
    status: 'running',
    profile_image: null,
  })

  const [errors, setErrors] = useState({})

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData) {
      console.log('UpdateRegistration: Initial Data received:', initialData)

      // registration_date या created_at में से जो भी मौजूद हो उसे "YYYY-MM-DD" फ़ॉर्मेट में बदलें
      let formattedRegDate = ''
      const rawRegDate = initialData.registration_date || initialData.created_at
      if (rawRegDate) {
        formattedRegDate = rawRegDate.split('T')[0]
      }

      // date_of_birth को फ़ॉर्मेट करें
      let formattedDOB = ''
      if (initialData.date_of_birth) {
        formattedDOB = initialData.date_of_birth.split('T')[0]
      }

      setFormData({
        enrollment_number: initialData.enrollment_number || '',
        registration_date: formattedRegDate,
        session:
          initialData.session ||
          `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
        name: initialData.name || '',
        date_of_birth: formattedDOB,
        gender: initialData.gender || '',
        father_name: initialData.father_name || '',
        father_occupation: initialData.father_occupation || '',
        mother_name: initialData.mother_name || '',
        phone_number: initialData.phone_number || '',
        parent_phone_number: initialData.parent_phone_number || '',
        email: initialData.email || '',
        class: initialData.class || '',
        course: initialData.course || '',
        course_duration:
          initialData.course_duration !== undefined
            ? initialData.course_duration.toString()
            : '',
        school_or_college_name: initialData.school_or_college_name || '',
        subject_or_stream: initialData.subject_or_stream || '',
        address: initialData.address || '',
        city: initialData.city || '',
        pin_code: initialData.pin_code || '',
        fee: initialData.fee !== undefined ? initialData.fee.toString() : '0',
        due_fee:
          initialData.due_fee !== undefined
            ? initialData.due_fee.toString()
            : '0',
        discount:
          initialData.discount !== undefined
            ? initialData.discount.toString()
            : '0',
        status: initialData.status || 'running',
        profile_image: initialData.profile_image || null,
      })
    }
  }, [initialData])

  const handleInputChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => {
      let finalValue = value
      if (name === 'course_duration') {
        finalValue = value === '' ? '' : parseInt(value, 10) || 0
      }

      const updated = { ...prev, [name]: finalValue }

      // फ़ीस और डिस्काउंट बदलने पर ड्यू बैलेंस की गणना करें
      if (name === 'fee' || name === 'discount') {
        const feeAmount = parseFloat(updated.fee) || 0
        const discountAmount = parseFloat(updated.discount) || 0
        updated.due_fee = (feeAmount - discountAmount).toString()
      }

      return updated
    })

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be smaller than 2MB')
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          profile_image: reader.result,
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.enrollment_number.trim()) {
      newErrors.enrollment_number = 'Enrollment number is required'
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!formData.father_name.trim()) {
      newErrors.father_name = 'Father name is required'
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required'
    } else if (!/^\d{10}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone number must be 10 digits'
    }
    if (!formData.class.trim()) {
      newErrors.class = 'Class is required'
    }
    if (!formData.registration_date) {
      newErrors.registration_date = 'Registration date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      let finalRegDate = formData.registration_date
      if (finalRegDate) {
        const dateObj = new Date(finalRegDate)
        finalRegDate = dateObj.toISOString()
      }

      let finalDOB = formData.date_of_birth
      if (finalDOB) {
        const dobObj = new Date(finalDOB)
        finalDOB = dobObj.toISOString()
      }

      const submissionData = {
        ...formData,
        registration_date: finalRegDate,
        created_at: finalRegDate, // बैकएंड कम्पैटिबिलिटी के लिए दोनों कीज़
        date_of_birth: finalDOB,
        fee: parseInt(formData.fee, 10) || 0,
        due_fee: parseInt(formData.due_fee, 10) || 0,
        discount: parseInt(formData.discount, 10) || 0,
        course_duration: parseInt(formData.course_duration, 10) || 0,
      }

      onSubmit(submissionData)
    }
  }

  // Common UI Classes
  const inputClasses =
    'w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-base sm:text-sm'
  const labelClasses =
    'block text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wider'
  const errorClasses = 'text-red-500 text-xs mt-1 font-medium'

  return (
    <div className='max-w-4xl mx-auto p-3 sm:p-6 md:p-8 bg-white shadow-xl rounded-xl sm:rounded-2xl border border-gray-100'>
      {/* Form Header */}
      <div className='mb-6 sm:mb-8 border-b pb-4'>
        <h1 className='text-2xl sm:text-3xl font-bold text-gray-950 mb-2'>
          Update Registration
        </h1>
        <p className='text-sm sm:text-base text-gray-500'>
          Update student registration and personal record information
        </p>
      </div>

      <form onSubmit={handleSubmit} className='space-y-6 sm:space-y-8'>
        {/* Section 1: Official Identifiers */}
        <div className='bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100'>
          <h2 className='text-lg font-bold text-gray-900 mb-4 flex items-center'>
            <span className='bg-blue-100 text-blue-700 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold mr-2'>
              1
            </span>
            Official Identifiers
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'>
            <div>
              <label htmlFor='registration_date' className={labelClasses}>
                Registration Date *
              </label>
              <input
                type='date'
                id='registration_date'
                name='registration_date'
                value={formData.registration_date}
                onChange={handleInputChange}
                className={inputClasses}
              />
              {errors.registration_date && (
                <p className={errorClasses}>{errors.registration_date}</p>
              )}
            </div>

            <div>
              <label htmlFor='enrollment_number' className={labelClasses}>
                Enrollment Number *
              </label>
              <input
                type='text'
                id='enrollment_number'
                name='enrollment_number'
                value={formData.enrollment_number}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='Enter enrollment number'
              />
              {errors.enrollment_number && (
                <p className={errorClasses}>{errors.enrollment_number}</p>
              )}
            </div>

            <div className='sm:col-span-2 md:col-span-1'>
              <label htmlFor='session' className={labelClasses}>
                Academic Session *
              </label>
              <input
                type='text'
                id='session'
                name='session'
                value={formData.session}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='e.g., 2024-2025'
              />
            </div>
          </div>
        </div>

        {/* Section 2: Student Personal Profile */}
        <div className='bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100'>
          <h2 className='text-lg font-bold text-gray-900 mb-4 flex items-center'>
            <span className='bg-blue-100 text-blue-700 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold mr-2'>
              2
            </span>
            Personal Information
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            <div className='md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <label htmlFor='name' className={labelClasses}>
                  Student Name *
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  className={inputClasses}
                  placeholder='Enter student name'
                />
                {errors.name && <p className={errorClasses}>{errors.name}</p>}
              </div>

              <div>
                <label htmlFor='date_of_birth' className={labelClasses}>
                  Date of Birth
                </label>
                <input
                  type='date'
                  id='date_of_birth'
                  name='date_of_birth'
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className={inputClasses}
                />
              </div>

              <div className='sm:col-span-2'>
                <label htmlFor='gender' className={labelClasses}>
                  Gender
                </label>
                <select
                  id='gender'
                  name='gender'
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={inputClasses}
                >
                  <option value=''>Select Gender</option>
                  <option value='Male'>Male</option>
                  <option value='Female'>Female</option>
                  <option value='Other'>Other</option>
                </select>
              </div>
            </div>

            {/* Profile Image Upload */}
            <div className='flex flex-col items-center justify-center border-2 border-dashed border-gray-200 p-4 rounded-xl bg-gray-50/20 w-full'>
              {formData.profile_image ? (
                <img
                  src={formData.profile_image}
                  alt='Student Preview'
                  className='w-24 h-24 object-cover rounded-lg shadow-md mb-3 border border-gray-300'
                />
              ) : (
                <div className='w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 flex items-center justify-center rounded-full text-gray-400 text-2xl sm:text-3xl mb-3'>
                  🧑‍🎓
                </div>
              )}
              <input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                onChange={handleFileChange}
                className='hidden'
                id='profile-image-upload'
              />
              <label
                htmlFor='profile-image-upload'
                className='cursor-pointer text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-all duration-200 inline-block text-center'
              >
                Change Photo
              </label>
            </div>
          </div>
        </div>

        {/* Section 3: Family & Contact Information */}
        <div className='bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100'>
          <h2 className='text-lg font-bold text-gray-900 mb-4 flex items-center'>
            <span className='bg-blue-100 text-blue-700 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold mr-2'>
              3
            </span>
            Family & Contact Information
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'>
            <div>
              <label htmlFor='father_name' className={labelClasses}>
                Father's Name *
              </label>
              <input
                type='text'
                id='father_name'
                name='father_name'
                value={formData.father_name}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder="Enter father's name"
              />
              {errors.father_name && (
                <p className={errorClasses}>{errors.father_name}</p>
              )}
            </div>

            <div>
              <label htmlFor='father_occupation' className={labelClasses}>
                Father's Occupation
              </label>
              <input
                type='text'
                id='father_occupation'
                name='father_occupation'
                value={formData.father_occupation}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='e.g., Business, Service'
              />
            </div>

            <div>
              <label htmlFor='mother_name' className={labelClasses}>
                Mother's Name
              </label>
              <input
                type='text'
                id='mother_name'
                name='mother_name'
                value={formData.mother_name}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder="Enter mother's name"
              />
            </div>

            <div>
              <label htmlFor='phone_number' className={labelClasses}>
                Student Phone *
              </label>
              <input
                type='tel'
                id='phone_number'
                name='phone_number'
                value={formData.phone_number}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='10-digit number'
              />
              {errors.phone_number && (
                <p className={errorClasses}>{errors.phone_number}</p>
              )}
            </div>

            <div>
              <label htmlFor='parent_phone_number' className={labelClasses}>
                Parent Phone
              </label>
              <input
                type='tel'
                id='parent_phone_number'
                name='parent_phone_number'
                value={formData.parent_phone_number}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='10-digit number'
              />
            </div>

            <div>
              <label htmlFor='email' className={labelClasses}>
                Email ID
              </label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='student@domain.com'
              />
            </div>
          </div>
        </div>

        {/* Section 4: Residential Address */}
        <div className='bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100'>
          <h2 className='text-lg font-bold text-gray-900 mb-4 flex items-center'>
            <span className='bg-blue-100 text-blue-700 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold mr-2'>
              4
            </span>
            Residential Address
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6'>
            <div className='sm:col-span-3 lg:col-span-1'>
              <label htmlFor='address' className={labelClasses}>
                Correspondence Address
              </label>
              <input
                type='text'
                id='address'
                name='address'
                value={formData.address}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='House, Street, Area'
              />
            </div>

            <div>
              <label htmlFor='city' className={labelClasses}>
                City / Town
              </label>
              <input
                type='text'
                id='city'
                name='city'
                value={formData.city}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='City'
              />
            </div>

            <div>
              <label htmlFor='pin_code' className={labelClasses}>
                Pincode
              </label>
              <input
                type='text'
                id='pin_code'
                name='pin_code'
                value={formData.pin_code}
                onChange={handleInputChange}
                maxLength='6'
                className={inputClasses}
                placeholder='6 Digits'
              />
            </div>
          </div>
        </div>

        {/* Section 5: Academic Information */}
        <div className='bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100'>
          <h2 className='text-lg font-bold text-gray-900 mb-4 flex items-center'>
            <span className='bg-blue-100 text-blue-700 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold mr-2'>
              5
            </span>
            Academic Information
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'>
            <div>
              <label htmlFor='class' className={labelClasses}>
                Class *
              </label>
              <input
                type='text'
                id='class'
                name='class'
                value={formData.class}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='e.g., 10th, 12th'
              />
              {errors.class && <p className={errorClasses}>{errors.class}</p>}
            </div>

            <div>
              <label htmlFor='course' className={labelClasses}>
                Course
              </label>
              <input
                type='text'
                id='course'
                name='course'
                value={formData.course}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='Enter course'
              />
            </div>

            <div>
              <label htmlFor='course_duration' className={labelClasses}>
                Course Duration (Months)
              </label>
              <input
                type='number'
                id='course_duration'
                name='course_duration'
                value={formData.course_duration}
                onChange={handleInputChange}
                min='0'
                className={inputClasses}
                placeholder='e.g., 3, 6, 12'
              />
            </div>

            <div>
              <label htmlFor='subject_or_stream' className={labelClasses}>
                Subject/Stream
              </label>
              <input
                type='text'
                id='subject_or_stream'
                name='subject_or_stream'
                value={formData.subject_or_stream}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='e.g., PCM, Commerce'
              />
            </div>

            <div className='sm:col-span-2 lg:col-span-1'>
              <label htmlFor='school_or_college_name' className={labelClasses}>
                Institution Name
              </label>
              <input
                type='text'
                id='school_or_college_name'
                name='school_or_college_name'
                value={formData.school_or_college_name}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='School/College name'
              />
            </div>
          </div>
        </div>

        {/* Section 6: Fee Details & Status */}
        <div className='bg-blue-50/40 p-4 sm:p-6 rounded-xl border border-blue-100'>
          <h2 className='text-lg font-bold text-gray-900 mb-4 flex items-center'>
            <span className='bg-blue-100 text-blue-700 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold mr-2'>
              6
            </span>
            Fee & Status Details
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6'>
            <div>
              <label htmlFor='fee' className={labelClasses}>
                Total Fee (₹)
              </label>
              <input
                type='number'
                id='fee'
                name='fee'
                value={formData.fee}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='Total Fee amount'
              />
            </div>

            <div>
              <label htmlFor='discount' className={labelClasses}>
                Discount (₹)
              </label>
              <input
                type='number'
                id='discount'
                name='discount'
                value={formData.discount}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder='0.00'
              />
            </div>

            <div>
              <label htmlFor='due_fee' className={labelClasses}>
                Due Fee Balance (₹)
              </label>
              <input
                type='number'
                id='due_fee'
                name='due_fee'
                value={formData.due_fee}
                onChange={handleInputChange}
                readOnly
                className='w-full px-4 py-2.5 sm:py-3 border border-gray-300 bg-gray-100 rounded-lg text-base sm:text-sm font-bold text-red-600 outline-none'
              />
            </div>

            <div>
              <label htmlFor='status' className={labelClasses}>
                Status *
              </label>
              <select
                id='status'
                name='status'
                value={formData.status}
                onChange={handleInputChange}
                className={inputClasses}
              >
                <option value='running'>Running</option>
                <option value='left'>Left</option>
                <option value='completed'>Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t border-gray-100'>
          <button
            type='button'
            className='w-full sm:w-auto px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 text-sm sm:text-base font-medium'
            onClick={() => onCancel()}
          >
            Cancel
          </button>

          <button
            type='submit'
            disabled={loading}
            className='w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base font-medium'
          >
            {loading ? (
              <>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                Updating...
              </>
            ) : (
              'Update Registration'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default UpdateRegistration
